/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import { FormattedMessage } from 'react-intl'
import { find, sumBy } from 'lodash'

import { EuiFormHelpText } from '@elastic/eui'

import type {
  ApmTopologyElement,
  ElasticsearchClusterTopologyElement,
  KibanaClusterTopologyElement,
  InstanceConfiguration,
} from '@modules/cloud-api/v1/types'
import { addToast } from '@modules/cui/Toasts'

import { getNodeRoles, isDedicatedMaster } from '@/lib/stackDeployments/selectors/nodeRoles'

import { isEnabledConfiguration, getInstanceCount } from './conversion'

type NodeConfiguration =
  | ElasticsearchClusterTopologyElement
  | KibanaClusterTopologyElement
  | ApmTopologyElement

interface HasAutoEnabledDedicatedMasterArgs {
  nodeConfiguration: NodeConfiguration
  dedicatedMasterThreshold?: number | null
}

export function getAutoEnabledDedicatedMastersNotifier(): (
  props: HasAutoEnabledDedicatedMasterArgs,
) => void {
  let hasInitialized = false
  let wasEnabled: boolean | null = null

  return (props: HasAutoEnabledDedicatedMasterArgs) => {
    const isEnabled = hasAutoEnabledDedicatedMaster(props)

    if (wasEnabled === isEnabled) {
      return
    }

    wasEnabled = isEnabled

    if (!hasInitialized) {
      hasInitialized = true
      return
    }

    const baseToast = {
      family: `automatically-enabled-dedicated-masters`,
      color: `primary`,
      iconType: `node`,
      title: (
        <FormattedMessage
          id='configure-instance.dedicated-masters-toast-label'
          defaultMessage='Dedicated masters'
        />
      ),
    }

    if (isEnabled) {
      addToast({
        ...baseToast,
        text: (
          <Fragment>
            <FormattedMessage
              id='configure-instance.dedicated-masters-toast-added-description'
              defaultMessage='Your deployment has reached a size that requires dedicated masters.'
            />

            <EuiFormHelpText>
              <FormattedMessage
                id='configure-instance.dedicated-masters-toast-added-note'
                defaultMessage='They have been automatically added to the configuration.'
              />
            </EuiFormHelpText>
          </Fragment>
        ),
      })

      return
    }

    addToast({
      ...baseToast,
      text: (
        <Fragment>
          <FormattedMessage
            id='configure-instance.dedicated-masters-toast-removed-description'
            defaultMessage='Your deployment no longer requires dedicated masters.'
          />

          <EuiFormHelpText>
            <FormattedMessage
              id='configure-instance.dedicated-masters-toast-removed-note'
              defaultMessage='They have been automatically removed from the configuration.'
            />
          </EuiFormHelpText>
        </Fragment>
      ),
    })
  }
}

function hasAutoEnabledDedicatedMaster({
  nodeConfiguration,
  dedicatedMasterThreshold,
}: HasAutoEnabledDedicatedMasterArgs) {
  const dedicatedMaster = isDedicatedMaster({ topologyElement: nodeConfiguration })
  const dedicatedMasterWithoutThreshold = dedicatedMaster && !dedicatedMasterThreshold

  if (dedicatedMasterWithoutThreshold) {
    return false
  }

  if (!dedicatedMaster) {
    return false
  }

  const enabled = isEnabledConfiguration(nodeConfiguration)

  return enabled
}

export function getTiebreakerMaster(instanceConfigurations): InstanceConfiguration {
  return instanceConfigurations.find(
    (nodeConfiguration) =>
      nodeConfiguration.node_types &&
      nodeConfiguration.node_types.length === 1 &&
      nodeConfiguration.node_types[0] === 'master' &&
      !nodeConfiguration.deleted_on,
  )
}

export function hasTiebreakerMaster(nodeConfigurations, instanceConfigurations) {
  const activeConfigurations = nodeConfigurations.filter((nodeConfiguration) => {
    const enabled = isEnabledConfiguration(nodeConfiguration)

    if (enabled === false) {
      return false
    }

    const { instance_configuration_id: id } = nodeConfiguration
    const instanceConfiguration = find(instanceConfigurations, {
      id,
    })

    return instanceConfiguration != null // sanity
  })
  const multiZoneMasterEligibleCounts = activeConfigurations.map((nodeConfiguration) => {
    const {
      instance_configuration_id: id,
      zone_count = 0,
      size,
      node_count_per_zone,
    } = nodeConfiguration

    const activeNodeTypes = getNodeRoles({ topologyElement: nodeConfiguration })

    if (!activeNodeTypes.includes(`master`)) {
      return 0
    }

    const instanceConfiguration = find(instanceConfigurations, {
      id,
    }) as InstanceConfiguration

    if (!instanceConfiguration) {
      return 0
    }

    const {
      instance_type,
      discrete_sizes: { sizes },
    } = instanceConfiguration

    if (instance_type !== `elasticsearch`) {
      return 0
    }

    const instanceCount = getInstanceCount({
      size: size ? size.value : undefined,
      sizes,
      exactInstanceCount: node_count_per_zone,
    })

    const masterEligibleCount =
      instanceCount * (typeof zone_count === `number` && zone_count > 1 ? zone_count : 0)

    return masterEligibleCount
  })

  const totalMultiZoneMasterEligibleCount = sumBy(multiZoneMasterEligibleCounts)

  if (totalMultiZoneMasterEligibleCount === 0) {
    return false // no multi-AZ master-eligibles means no tiebreaker needed
  }

  if (totalMultiZoneMasterEligibleCount % 2 !== 0) {
    return false // odd number of multi-AZ master-eligibles means no tiebreaker needed
  }

  return true
}
